import {isNotNullOrUndefined, noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import {Pagination} from 'lib/Listings';
import {GRAY_DARK, GRAY_DARKER, hexToRgba, NEAR_BLACK} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {BasicEvent} from 'util/Types';

export interface ResultsPagerProps {
  allowFastForwardAndBackward?: boolean;
  ariaLabel?: string;
  isLoading: boolean;
  setOffset: (offset: number) => void;

  pagination: Pagination;
  subtext?: string | null;
  /**
   * Singular word used to describe rows of the table. If null, no description will be shown.
   * @default 'item'
   */
  resultDescription?: string | null;
}

function InnerResultsPager(
  props: Pick<ResultsPagerProps, 'allowFastForwardAndBackward' | 'isLoading' | 'pagination' | 'setOffset'>
): JSX.Element {
  const showOffset = (e: BasicEvent, offset: number): void => {
    e.preventDefault();
    props.setOffset(offset);
  };

  const {allowFastForwardAndBackward, pagination} = props;
  const prevOffset = pagination.offset - pagination.limit;
  const nextOffset = pagination.offset + pagination.limit;
  const disablePrev = prevOffset < 0 || props.isLoading;
  const disableNext = nextOffset >= pagination.total || props.isLoading;
  const startOfLastPage =
    pagination.total % pagination.limit === 0
      ? pagination.total - pagination.limit
      : Math.floor(pagination.total / pagination.limit) * pagination.limit;

  return (
    <ul className="pager u-no-select">
      {allowFastForwardAndBackward && (
        <>
          <li className={classNames({disabled: disablePrev}, 'u-tiny-spacing-right')}>
            {disablePrev ? (
              <BenchlingIcon
                altText="First page"
                aria-disabled={true}
                icon="arrow-first-page"
                className={css(resultsPagerStyles.iconButton, resultsPagerStyles.faded)}
                {...getActionButtonRoleProps({onPress: noop})}
              />
            ) : (
              <BenchlingIcon
                altText="First page"
                className={classNames(
                  css(resultsPagerStyles.iconButton, resultsPagerStyles.enabled),
                  'u-pointer'
                )}
                icon="arrow-first-page"
                {...getActionButtonRoleProps({
                  onPress: (e) => {
                    showOffset(e, 0);
                  },
                })}
              />
            )}
          </li>{' '}
        </>
      )}
      <li className={classNames({disabled: disablePrev}, 'u-tiny-spacing-right')}>
        {disablePrev ? (
          <BenchlingIcon
            altText="Previous page"
            aria-disabled={true}
            icon="chevron-left"
            className={classNames(css(resultsPagerStyles.iconButton), 'faded')}
            {...getActionButtonRoleProps({onPress: noop})}
          />
        ) : (
          <BenchlingIcon
            altText="Previous page"
            icon="chevron-left"
            className={classNames(css(resultsPagerStyles.iconButton), 'u-pointer')}
            {...getActionButtonRoleProps({
              onPress: (e) => {
                showOffset(e, prevOffset);
              },
            })}
          />
        )}
      </li>{' '}
      <li className={classNames({disabled: disableNext}, 'u-tiny-spacing-left')}>
        {disableNext ? (
          <BenchlingIcon
            altText="Next page"
            aria-disabled={true}
            icon="chevron-right"
            className={classNames(css(resultsPagerStyles.iconButton), 'faded')}
            {...getActionButtonRoleProps({onPress: noop})}
          />
        ) : (
          <BenchlingIcon
            altText="Next page"
            icon="chevron-right"
            className={classNames(css(resultsPagerStyles.iconButton), 'u-pointer')}
            {...getActionButtonRoleProps({
              onPress: (e) => {
                showOffset(e, nextOffset);
              },
            })}
          />
        )}
      </li>
      {allowFastForwardAndBackward && (
        <>
          {' '}
          <li className={classNames({disabled: disableNext}, 'u-tiny-spacing-left')}>
            {disableNext ? (
              <BenchlingIcon
                altText="Last page"
                aria-disabled={true}
                icon="arrow-last-page"
                className={css(resultsPagerStyles.iconButton, resultsPagerStyles.faded)}
                {...getActionButtonRoleProps({onPress: noop})}
              />
            ) : (
              <BenchlingIcon
                altText="Last page"
                className={classNames(
                  css(resultsPagerStyles.iconButton, resultsPagerStyles.enabled),
                  'u-pointer'
                )}
                icon="arrow-last-page"
                {...getActionButtonRoleProps({
                  onPress: (e) => {
                    showOffset(e, startOfLastPage);
                  },
                })}
              />
            )}
          </li>
        </>
      )}
    </ul>
  );
}

function ResultsPager(props: ResultsPagerProps): JSX.Element {
  const {pagination, resultDescription = 'item'} = props;
  let start;
  let end;
  if (pagination.total > 0) {
    start = pagination.offset + 1; // 1-indexed
    end = Math.min(start + pagination.limit - 1, pagination.total); // End-inclusive.
  } else {
    start = 0;
    end = 0;
  }
  const description = resultDescription != null ? pluralize(pagination.total, resultDescription) : '';

  const isExactText = isNotNullOrUndefined(pagination.isExact) && !pagination.isExact ? 'more than ' : '';
  const pageText = `${start}-${end} of ${isExactText}${pagination.total} ${description}`;
  return (
    <nav aria-label={props.ariaLabel ?? description} className="resultsPager u-input-line-height">
      <InnerResultsPager
        allowFastForwardAndBackward={props.allowFastForwardAndBackward}
        isLoading={props.isLoading}
        pagination={props.pagination}
        setOffset={props.setOffset}
      />
      {!props.isLoading && (
        <div className={classNames('u-small-spacing-left', css(resultsPagerStyles.pageDetails))}>
          <span>{pageText}</span>
          {props.subtext && <span>, {props.subtext}</span>}
        </div>
      )}
    </nav>
  );
}

export default ResultsPager;

export const resultsPagerStyles = StyleSheet.create({
  iconButton: {
    ':focus': {
      outline: 'none',
    },
    ':focus-visible': {
      boxShadow: `0 2px 6px 0 rgba(${hexToRgba(GRAY_DARK, 0.5)})`,
    },
  },
  enabled: {
    color: NEAR_BLACK,
  },
  faded: {
    color: GRAY_DARK,
  },
  pageDetails: {
    color: GRAY_DARKER,
    display: 'inline-block',
  },
});
